import create from "zustand";

const READ_STORAGE_PREFIX = "oppizi-comments-readAt:";

/**
 * localStorage-backed map of thread id → last read timestamp (ms). Keyed by
 * userId so multiple accounts on the same browser don't stomp each other.
 */
const loadReadMap = (userId: string | null): Record<string, number> => {
  if (!userId || typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(READ_STORAGE_PREFIX + userId);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const saveReadMap = (userId: string | null, map: Record<string, number>) => {
  if (!userId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(READ_STORAGE_PREFIX + userId, JSON.stringify(map));
  } catch {
    /* quota / SSR — ignore */
  }
};

type CommentsState = {
  /**
   * Figma-like placing mode. When true, the next click on the canvas
   * drops a new comment pin.
   */
  isPlacing: boolean;
  /** Currently opened thread (pin popover is visible for this one). */
  openThreadId: string | null;
  /**
   * Thread id + nonce the canvas should recenter to. Increments whenever
   * the sidebar requests the canvas to focus a specific thread.
   */
  focusThreadRequest: { threadId: string; nonce: number } | null;
  /** Show resolved pins on the canvas. */
  showResolved: boolean;
  /** When true, hide all comment pins on the canvas. Toggled with Shift+C. */
  pinsHidden: boolean;
  /** Right-side history panel visibility. */
  isHistoryOpen: boolean;
  /** How the threads list in the right panel is ordered. */
  sortBy: "date" | "unread";
  /** When true, only show threads the current user created or participated in. */
  onlyMine: boolean;

  /**
   * Per-thread "last read at" timestamps (ms). Persisted to localStorage
   * under the current user's key. Used to compute unread status for ANY
   * thread — including ones the user never participated in or was tagged on,
   * where Liveblocks' own subscription cursor isn't set.
   */
  threadReadAt: Record<string, number>;
  /** The user id whose read map is currently loaded. */
  currentReadUserId: string | null;

  /**
   * Total unread thread count across all threads in the room. Computed by the
   * always-mounted unread aggregator in `CommentsOverlay` and read by UI
   * elements (e.g. the tool island button badge).
   */
  totalUnreadCount: number;

  /**
   * Design context used by comment-mention email notifications. Populated by
   * the builder (Tldraw.tsx) whenever the current design changes so the
   * comments feature can include the design's name + a deep link without
   * having to plumb props through.
   */
  designContext: {
    designId: string | number | null;
    designName: string | null;
    designUrl: string | null;
  };

  enterPlacingMode: () => void;
  exitPlacingMode: () => void;
  togglePlacingMode: () => void;

  setOpenThreadId: (threadId: string | null) => void;
  /** Set open thread AND request the canvas to pan/center on it. */
  focusThread: (threadId: string) => void;
  toggleShowResolved: () => void;
  setPinsHidden: (hidden: boolean) => void;
  togglePinsHidden: () => void;

  setSortBy: (sort: "date" | "unread") => void;
  toggleOnlyMine: () => void;

  openHistory: () => void;
  closeHistory: () => void;
  toggleHistory: () => void;

  /** Load the read map for a specific user (call on login / app mount). */
  hydrateReadMap: (userId: string | null) => void;
  /** Stamp a thread as "read now" for the current user. */
  markThreadRead: (threadId: string) => void;
  /** Drop a thread from the read map (call after deleteThread so any stale
   *  unread badge tied to the deleted id is cleaned up). */
  forgetThread: (threadId: string) => void;
  /** Update total unread count (called by the aggregator in CommentsOverlay). */
  setTotalUnreadCount: (n: number) => void;

  setDesignContext: (ctx: {
    designId: string | number | null;
    designName: string | null;
    designUrl: string | null;
  }) => void;
};

export const useCommentsStore = create<CommentsState>((set, get) => ({
  isPlacing: false,
  openThreadId: null,
  focusThreadRequest: null,
  showResolved: false,
  pinsHidden: false,
  isHistoryOpen: false,
  sortBy: "date",
  onlyMine: false,
  threadReadAt: {},
  currentReadUserId: null,
  totalUnreadCount: 0,
  designContext: { designId: null, designName: null, designUrl: null },

  enterPlacingMode: () => {
    set({ isPlacing: true, openThreadId: null, isHistoryOpen: true });
    if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("openCommentsPanel"));
  },
  exitPlacingMode: () => set({ isPlacing: false }),
  togglePlacingMode: () => {
    const next = !get().isPlacing;
    set({
      isPlacing: next,
      openThreadId: null,
      isHistoryOpen: next ? true : get().isHistoryOpen,
    });
    if (next && typeof window !== "undefined") window.dispatchEvent(new CustomEvent("openCommentsPanel"));
  },

  setOpenThreadId: (threadId) => set({ openThreadId: threadId, isPlacing: false }),
  focusThread: (threadId) =>
    set({
      openThreadId: threadId,
      isPlacing: false,
      focusThreadRequest: { threadId, nonce: Date.now() },
    }),
  toggleShowResolved: () => set({ showResolved: !get().showResolved }),
  setPinsHidden: (hidden) => set({ pinsHidden: hidden, openThreadId: hidden ? null : get().openThreadId }),
  togglePinsHidden: () => {
    const next = !get().pinsHidden;
    set({ pinsHidden: next, openThreadId: next ? null : get().openThreadId });
  },

  setSortBy: (sort) => set({ sortBy: sort }),
  toggleOnlyMine: () => set({ onlyMine: !get().onlyMine }),

  openHistory: () => set({ isHistoryOpen: true }),
  closeHistory: () => set({ isHistoryOpen: false }),
  toggleHistory: () => set({ isHistoryOpen: !get().isHistoryOpen }),

  hydrateReadMap: (userId) => {
    if (get().currentReadUserId === userId) return;
    set({ currentReadUserId: userId, threadReadAt: loadReadMap(userId) });
  },
  markThreadRead: (threadId) => {
    const { currentReadUserId, threadReadAt } = get();
    const next = { ...threadReadAt, [threadId]: Date.now() };
    set({ threadReadAt: next });
    saveReadMap(currentReadUserId, next);
  },
  forgetThread: (threadId) => {
    const { currentReadUserId, threadReadAt, openThreadId } = get();
    if (!(threadId in threadReadAt) && openThreadId !== threadId) return;
    const next = { ...threadReadAt };
    delete next[threadId];
    set({
      threadReadAt: next,
      openThreadId: openThreadId === threadId ? null : openThreadId,
    });
    saveReadMap(currentReadUserId, next);
  },
  setTotalUnreadCount: (n) => {
    if (get().totalUnreadCount === n) return;
    set({ totalUnreadCount: n });
  },
  setDesignContext: (ctx) => {
    const prev = get().designContext;
    if (prev.designId === ctx.designId && prev.designName === ctx.designName && prev.designUrl === ctx.designUrl) {
      return;
    }
    set({ designContext: ctx });
  },
}));
